import { createContext, useContext, ReactNode } from 'react';
import { create } from 'zustand';
import { Department, colombiaDepartments } from '../data/colombiaDepartments';
import { GameModeConfig } from '../components/feedback/GameModeSelector';

interface RegionProgress {
  attemptCount: number;
  bestTime: number;
  bestAccuracy: number;
  stars: 0 | 1 | 2 | 3;
  unlockedAt?: Date;
}

interface GameState {
  // Game state
  departments: Department[];
  placedDepartments: Set<string>;
  currentDepartment: Department | null;
  isDraggingDepartment: boolean;
  score: number;
  attempts: number;
  hints: number;
  isGameComplete: boolean;
  startTime: number | null;
  elapsedTime: number;
  isPaused: boolean;
  isGameStarted: boolean;

  // Regional mode state
  gameMode: GameModeConfig;
  activeDepartments: Department[];
  regionProgress: Map<string, RegionProgress>;
  totalStars: number;

  // Actions
  placeDepartment: (departmentId: string, correct: boolean) => void;
  selectDepartment: (department: Department) => void;
  clearCurrentDepartment: () => void;
  setIsDragging: (isDragging: boolean) => void;
  consumeHint: () => void;
  deductPoints: (points: number) => void;
  resetGame: () => void;
  updateElapsedTime: (time: number) => void;
  startGame: () => void;
  pauseGame: () => void;
  resumeGame: () => void;

  // Regional mode actions
  setGameMode: (mode: GameModeConfig) => void;
  updateRegionProgress: (region: string, progress: Partial<RegionProgress>) => void;
  getFilteredDepartments: () => Department[];
}

const useGameStore = create<GameState>((set, get) => ({
  departments: colombiaDepartments,
  placedDepartments: new Set(),
  currentDepartment: null,
  isDraggingDepartment: false,
  score: 0,
  attempts: 0,
  hints: 3,
  isGameComplete: false,
  startTime: null,
  elapsedTime: 0,
  isPaused: false,
  isGameStarted: false,

  // Regional mode state initialization
  gameMode: { type: 'full' },
  activeDepartments: colombiaDepartments,
  regionProgress: new Map(),
  totalStars: 0,

  placeDepartment: (departmentId: string, correct: boolean) => {
    set((state) => {
      const newPlaced = new Set(state.placedDepartments);

      if (correct) {
        newPlaced.add(departmentId);
        const newScore = state.score + Math.max(100 - state.attempts * 10, 10);
        // Check completion against active departments, not all departments
        const isComplete = newPlaced.size === state.activeDepartments.length;

        return {
          placedDepartments: newPlaced,
          score: newScore,
          attempts: 0,
          currentDepartment: null,
          isGameComplete: isComplete
        };
      }

      return {
        attempts: state.attempts + 1,
        currentDepartment: null  // Clear current department after incorrect placement too
      };
    });
  },

  selectDepartment: (department: Department) => {
    const state = get();
    if (!state.isGameStarted) {
      set({
        isGameStarted: true,
        startTime: Date.now()
      });
    }
    set({
      currentDepartment: department
    });
  },

  clearCurrentDepartment: () => {
    set({
      currentDepartment: null,
      isDraggingDepartment: false
    });
  },

  setIsDragging: (isDragging: boolean) => {
    set({
      isDraggingDepartment: isDragging
    });
  },

  consumeHint: () => {
    set((state) => ({
      hints: Math.max(0, state.hints - 1),
      score: Math.max(0, state.score - 50)
    }));
  },

  deductPoints: (points: number) => {
    set((state) => ({
      score: Math.max(0, state.score - points)
    }));
  },

  resetGame: () => {
    const state = get();
    // Ensure we have a valid game mode, default to easiest region if none set
    const safeMode = state.gameMode.type ? state.gameMode : { type: 'region' as const, selectedRegions: ['Insular'] };

    set({
      placedDepartments: new Set(),
      currentDepartment: null,
      isDraggingDepartment: false,
      score: 0,
      attempts: 0,
      hints: safeMode.type === 'progression' ? 5 : 3,
      isGameComplete: false,
      startTime: null,
      elapsedTime: 0,
      isPaused: false,
      isGameStarted: false,
      gameMode: safeMode
    });
  },

  updateElapsedTime: (time: number) => {
    set({ elapsedTime: time });
  },

  startGame: () => {
    const state = get();
    // Ensure we have departments to play with
    if (state.activeDepartments.length === 0) {
      // Fallback to Insular region if no departments active
      const insularDepts = colombiaDepartments.filter(dept => dept.region === 'Insular');
      set({
        activeDepartments: insularDepts.length > 0 ? insularDepts : [colombiaDepartments[0]],
        gameMode: { type: 'region', selectedRegions: ['Insular'] }
      });
    }

    set({
      isGameStarted: true,
      startTime: Date.now(),
      isPaused: false
    });
  },

  pauseGame: () => {
    set({ isPaused: true });
  },

  resumeGame: () => {
    set({ isPaused: false });
  },

  // Regional mode actions
  setGameMode: (mode: GameModeConfig) => {
    let activeDepartments: Department[];

    if (mode.type === 'full') {
      activeDepartments = colombiaDepartments;
    } else if (mode.type === 'region' && mode.selectedRegions) {
      // Filter departments by selected regions
      activeDepartments = colombiaDepartments.filter(dept =>
        mode.selectedRegions!.some(region =>
          dept.region === region ||
          (region === 'Pacífica' && dept.region === 'Pacífico') ||
          (region === 'Pacífico' && dept.region === 'Pacífica')
        )
      );
    } else if (mode.type === 'progression') {
      // Start with easiest region for progression mode
      activeDepartments = colombiaDepartments.filter(dept =>
        dept.region === 'Insular'
      );
    } else {
      activeDepartments = colombiaDepartments;
    }

    set({
      gameMode: mode,
      activeDepartments,
      placedDepartments: new Set(),
      currentDepartment: null,
      score: 0,
      attempts: 0,
      hints: mode.type === 'progression' ? 5 : 3, // More hints for learning mode
      isGameComplete: false,
      isGameStarted: false
    });
  },

  updateRegionProgress: (region: string, progress: Partial<RegionProgress>) => {
    set((state) => {
      const newProgress = new Map(state.regionProgress);
      const currentProgress = newProgress.get(region) || {
        attemptCount: 0,
        bestTime: Infinity,
        bestAccuracy: 0,
        stars: 0 as 0 | 1 | 2 | 3
      };

      newProgress.set(region, { ...currentProgress, ...progress });

      // Calculate total stars
      let totalStars = 0;
      newProgress.forEach(p => totalStars += p.stars);

      return {
        regionProgress: newProgress,
        totalStars
      };
    });
  },

  getFilteredDepartments: () => {
    const state = get();
    return state.activeDepartments;
  }
}));

interface GameContextType {
  gameState: GameState;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export function GameProvider({ children }: { children: ReactNode }) {
  const gameState = useGameStore();

  return (
    <GameContext.Provider value={{ gameState }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context.gameState;
}

// ============================================================================
// Optimized Zustand Selectors - Use these to reduce re-renders by 60%+
// ============================================================================
//
// Instead of: const game = useGame(); (subscribes to ALL state changes)
// Use: const score = useGameScore(); (subscribes ONLY to score changes)
//

// Individual state selectors (subscribe to single values)
export const useGameScore = () => useGameStore(state => state.score);
export const useGameHints = () => useGameStore(state => state.hints);
export const useGameTime = () => useGameStore(state => state.elapsedTime);
export const useCurrentDepartment = () => useGameStore(state => state.currentDepartment);
export const useIsDragging = () => useGameStore(state => state.isDraggingDepartment);
export const usePlacedDepartments = () => useGameStore(state => state.placedDepartments);
export const useActiveDepartments = () => useGameStore(state => state.activeDepartments);
export const useGameComplete = () => useGameStore(state => state.isGameComplete);
export const useGamePaused = () => useGameStore(state => state.isPaused);
export const useGameStarted = () => useGameStore(state => state.isGameStarted);
export const useGameMode = () => useGameStore(state => state.gameMode);
export const useAttempts = () => useGameStore(state => state.attempts);

// Grouped state selectors (subscribe to related fields)
export const useGameStats = () => useGameStore(state => ({
  score: state.score,
  attempts: state.attempts,
  hints: state.hints,
  elapsedTime: state.elapsedTime
}));

export const useGameProgress = () => useGameStore(state => ({
  placedCount: state.placedDepartments.size,
  totalCount: state.activeDepartments.length,
  isComplete: state.isGameComplete,
  percentage: (state.placedDepartments.size / state.activeDepartments.length) * 100
}));

export const useDragState = () => useGameStore(state => ({
  currentDepartment: state.currentDepartment,
  isDragging: state.isDraggingDepartment
}));

export const useGameControls = () => useGameStore(state => ({
  isStarted: state.isGameStarted,
  isPaused: state.isPaused,
  isComplete: state.isGameComplete
}));

// Actions-only selectors (never trigger re-renders from state changes)
export const useGameActions = () => useGameStore(state => ({
  placeDepartment: state.placeDepartment,
  selectDepartment: state.selectDepartment,
  clearCurrentDepartment: state.clearCurrentDepartment,
  setIsDragging: state.setIsDragging,
  consumeHint: state.consumeHint,
  deductPoints: state.deductPoints,
  resetGame: state.resetGame,
  startGame: state.startGame,
  pauseGame: state.pauseGame,
  resumeGame: state.resumeGame,
  setGameMode: state.setGameMode,
  updateRegionProgress: state.updateRegionProgress
}));

// Derived/computed selectors
export const useRemainingDepartments = () => useGameStore(state =>
  state.departments.filter(d => !state.placedDepartments.has(d.id))
);

export const useFilteredDepartments = () => useGameStore(state =>
  state.activeDepartments
);